import {
  EXPEDITED_PROGRAM,
  EXPEDITED_PROGRAM_GROUP_KEY as eg,
  EXPEDITED_PROGRAM_CONTENTS as ec,
} from "@FillInTableContainer/shared/constants/expeditedProgramKey.constants";
import { useModalityCategoryValue } from "@FillInTableContainer/shared/context/ModalityCategoryContext";
import {
  useTableCreatorFocusStatus,
  DEFAULT_FOCUS_STATUS,
} from "@FillInTableContainer/shared/context/TableCreatorFocusStatusContext";
import {
  useTableCreatorPositionInfo,
  DEFAULT_POSITION_INFO,
} from "@FillInTableContainer/shared/context/TableCreatorPositionInfoContext";
import {
  ValidatedInfoList,
  useValidatedInTableCreator,
} from "@FillInTableContainer/shared/context/ValidatedInTableCreatorContext";
import { useEntryDataValue } from "@stores/entryData";
import { getPressIdFromURL } from "@utils/getStateFromURL";

interface UseCheckValidationInTableCreatorReturn {
  handleCheckValidationFull: () => boolean;
  handleCheckValidationHalf: () => boolean;
}

export const useCheckValidationInExpeditedProgram =
  (): UseCheckValidationInTableCreatorReturn => {
    const entryData = useEntryDataValue(getPressIdFromURL());
    const { changeValidatedInfo } = useValidatedInTableCreator();
    const { changeInfo } = useTableCreatorPositionInfo();
    const { changeFocusStatus } = useTableCreatorFocusStatus();
    const { updateModalityCategory } = useModalityCategoryValue();

    const checkModalityCategory = () => {
      const drugProfileTableList =
        entryData?.[EXPEDITED_PROGRAM]?.[eg.drugProfile];
      const modalityValues = drugProfileTableList?.map((table) =>
        table.Modality.value?.map((shell) => shell.text),
      );

      const valueInfo = modalityValues?.flatMap((table, tableIndex) =>
        table.map((shell) => {
          if (updateModalityCategory[shell] === "") {
            return {
              groupTableKey: "Drug Profile",
              groupTableIndex: 0,
              tableKey: "Drug",
              tableIndex,
              groupShellKey: null,
              groupShellIndex: 0,
              shellKey: "Modality",
              shellIndex: 0,
              valueIndex: null,
              type: "alert",
              container: "shell",
            };
          }
          return null;
        }),
      );
      return valueInfo?.filter((item) => item !== null);
    };

    const checkExpeditedProgramContentsTable = () => {
      const expeditedProgramContentsTableData =
        entryData?.[EXPEDITED_PROGRAM]?.[eg.expeditedProgramContents];

      const sortedValidatedInfo = () => {
        const valueInfo =
          expeditedProgramContentsTableData?.flatMap(
            (tableInfo, tableIndex) => {
              const isValidatedRegulatoryAgency =
                !tableInfo?.[ec.regulatoryAgency].value.length;
              const isValidatedExpeditedProgramName =
                !tableInfo?.[ec.expeditedProgram].value.length;
              const tempValidatedInfo: ValidatedInfoList = [];
              if (isValidatedRegulatoryAgency)
                tempValidatedInfo.push({
                  groupTableKey: eg.expeditedProgramContents,
                  groupTableIndex: 1,
                  tableKey: "Program Contents",
                  tableIndex,
                  groupShellKey: "first",
                  groupShellIndex: 0,
                  shellKey: ec.regulatoryAgency,
                  shellIndex: 0,
                  valueIndex: null,
                  type: "alert",
                  container: "shell",
                });
              if (isValidatedExpeditedProgramName)
                tempValidatedInfo.push({
                  groupTableKey: eg.expeditedProgramContents,
                  groupTableIndex: 1,
                  tableKey: "Program Contents",
                  tableIndex,
                  groupShellKey: "second",
                  groupShellIndex: 0,
                  shellKey: ec.expeditedProgram,
                  shellIndex: 0,
                  valueIndex: null,
                  type: "alert",
                  container: "shell",
                });

              return tempValidatedInfo;
            },
          ) || [];

        return valueInfo;
      };

      return sortedValidatedInfo();
    };

    const handleCheckValidationHalf = () => {
      if (checkModalityCategory()?.length === 0) {
        changeInfo(DEFAULT_POSITION_INFO);
        changeFocusStatus(DEFAULT_FOCUS_STATUS);
        return true;
      }

      const validatedList = [...(checkModalityCategory() || [])];

      changeValidatedInfo(validatedList as ValidatedInfoList);
      return false;
    };

    const handleCheckValidationFull = () => {
      if (checkExpeditedProgramContentsTable().length === 0) {
        changeInfo(DEFAULT_POSITION_INFO);
        changeFocusStatus(DEFAULT_FOCUS_STATUS);
        return true;
      }

      const validatedList = [
        ...(checkModalityCategory() || []),
        ...(checkExpeditedProgramContentsTable() || []),
      ];

      changeValidatedInfo(validatedList as ValidatedInfoList);
      return false;
    };

    return {
      handleCheckValidationHalf,
      handleCheckValidationFull,
    };
  };
